/**
 * RecordingsSkeletons — layout-matched loading placeholders for the recordings
 * routes. Same contract as EventsSkeletons (read its header): each skeleton is
 * used as BOTH the route Suspense fallback / `pendingComponent` AND the
 * in-component `isLoading` state, so a slow chunk download followed by a data
 * fetch reads as ONE continuous structural skeleton instead of spinner →
 * skeleton → content. No `PageLoader`, no spinner, anywhere on these routes.
 *
 * The `*ContentSkeleton` exports sit inside nothing — the caller supplies the
 * column — and the full-page exports wrap them in the exact `max-w-3xl` column
 * the real pages use, so content settles without a layout jump.
 *
 * Placeholders use the shared <Skeleton> primitive (warm `bg-primary/10`).
 */

import { Skeleton } from '@/components/ui/skeleton';
// The pill bar is shared across /community, /events and /recordings, so its
// shell lives in components/shared — see PillBarShell / CommunityPillBar.
import { PillBarShell } from '@/components/shared/PillBarShell';

/** Staggered title widths so the rows read as content, not as a bar chart. */
const ROW_TITLE_WIDTHS = ['w-3/5', 'w-4/5', 'w-2/3'] as const;

/**
 * Recordings list content — header, search, chips, hero and three rows.
 *
 * The three rows match the real row geometry: a 112×63 poster on mobile /
 * 160×90 from `sm:` up, then the stacked series+date eyebrow, title and host
 * bars, with the fixed 36px copy-link lane on the right.
 */
/** `inTab`: the title is a section heading (Learn's Recordings tab), so its bar is that height. */
export function RecordingsListContentSkeleton({ inTab = false }: { inTab?: boolean } = {}) {
  return (
    <div aria-busy="true" aria-label="Loading recordings">
      <span className="sr-only">Loading recordings…</span>

      {/* Header: title + count on the left, search on the right. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <Skeleton className={inTab ? 'h-7 w-36 sm:h-8' : 'h-[34px] w-44 sm:h-[44px]'} />
          <Skeleton className="mt-2 h-3.5 w-52" />
        </div>
        <Skeleton className="h-[42px] w-full rounded-[10px] sm:w-[300px] sm:flex-shrink-0" />
      </div>

      {/* Series chips. */}
      <div className="mt-5 flex items-center gap-2 overflow-hidden">
        {['w-14', 'w-32', 'w-28', 'w-36', 'w-24'].map((w, i) => (
          <Skeleton key={i} className={`h-[30px] ${w} flex-shrink-0 rounded-full`} />
        ))}
      </div>

      {/* Latest hero. */}
      <div className="mt-6 flex flex-col gap-4 rounded-[18px] border border-borderStrong bg-card p-4 sm:flex-row sm:gap-5">
        <Skeleton className="aspect-video w-full rounded-[12px] sm:aspect-auto sm:h-[180px] sm:w-[320px] sm:flex-shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-2.5 w-28" />
          <Skeleton className="h-[22px] w-4/5" />
          <Skeleton className="h-3.5 w-48" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <div className="mt-auto flex items-center justify-between gap-3 pt-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full" />
              <Skeleton className="h-3.5 w-24" />
            </div>
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-[40px] w-[100px] rounded-[10px]" />
              <Skeleton className="h-[40px] w-[116px] rounded-[10px]" />
            </div>
          </div>
        </div>
      </div>

      {/* Month group header + rows. */}
      <div className="mt-8 flex items-center gap-3">
        <Skeleton className="h-2.5 w-28 flex-shrink-0" />
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="mt-3 space-y-2">
        {ROW_TITLE_WIDTHS.map((titleWidth, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-[14px] border border-border bg-card p-2.5 sm:gap-4 sm:p-3"
          >
            <Skeleton className="h-[63px] w-[112px] flex-shrink-0 rounded-[9px] sm:h-[90px] sm:w-[160px]" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-2.5 w-[140px]" />
              <Skeleton className={`mt-[7px] h-[15px] ${titleWidth}`} />
              <div className="mt-2.5 flex items-center gap-2">
                <Skeleton className="h-7 w-7 flex-shrink-0 rounded-full" />
                <Skeleton className="h-3 w-[104px]" />
              </div>
            </div>
            <Skeleton className="h-9 w-9 flex-shrink-0 rounded-[9px]" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Recording detail content — the player, the header block (series + date,
 * title, host row with its actions) and the session-recap card. Mirrors the
 * real article's above-the-fold blocks so nothing jumps when data lands.
 */
export function RecordingDetailContentSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading recording">
      <span className="sr-only">Loading recording…</span>

      {/* Breadcrumb. */}
      <Skeleton className="h-3.5 w-52" />

      {/* Player. */}
      <Skeleton className="mt-4 aspect-video w-full rounded-[16px]" />

      {/* Header block. */}
      <div className="mt-6 space-y-2.5">
        <Skeleton className="h-3.5 w-56" />
        <Skeleton className="h-7 w-11/12 sm:h-8" />
        <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full" />
            <Skeleton className="h-3.5 w-36" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-[36px] w-[104px] rounded-[10px]" />
            <Skeleton className="h-[36px] w-[96px] rounded-[10px]" />
          </div>
        </div>
      </div>

      {/* Session recap card. */}
      <div className="mt-[22px] rounded-[16px] border border-border bg-card p-6">
        <Skeleton className="h-2.5 w-28" />
        <div className="mt-3 space-y-2">
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-11/12" />
          <Skeleton className="h-3.5 w-3/4" />
        </div>
        <div className="mt-4 space-y-2.5">
          {['w-4/5', 'w-3/5', 'w-2/3'].map((w, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <Skeleton className="h-1.5 w-1.5 flex-shrink-0 rounded-full" />
              <Skeleton className={`h-3 ${w}`} />
            </div>
          ))}
        </div>
      </div>

      {/* "More from {series}" — header row + three cards. */}
      <div className="mt-8">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3.5 w-20 flex-shrink-0" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {['w-full', 'w-4/5', 'w-11/12'].map((w, i) => (
            <div key={i} className={i === 2 ? 'hidden space-y-2 sm:block' : 'space-y-2'}>
              <Skeleton className="h-[104px] w-full rounded-[10px]" />
              <Skeleton className={`h-3.5 ${w}`} />
              <Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Full-page list skeleton — chrome shell + list content, matching the outer
 * structure of pages/Recordings.tsx. Used as the `/recordings` route
 * `pendingComponent` (the capability gate runs before the component exists) and
 * as its Suspense fallback.
 */
export function RecordingsListSkeleton() {
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-background overflow-hidden">
      <PillBarShell />
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 pt-7 pb-16 sm:px-6">
          <RecordingsListContentSkeleton />
        </div>
      </div>
    </div>
  );
}

/**
 * Full-page detail skeleton — chrome shell + detail content, matching the outer
 * structure of pages/RecordingDetail.tsx. Used as the
 * `/recordings/$source/$id` route `pendingComponent` and Suspense fallback.
 */
export function RecordingDetailSkeleton() {
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-background overflow-hidden">
      <PillBarShell />
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 pt-6 pb-16 sm:px-6">
          <RecordingDetailContentSkeleton />
        </div>
      </div>
    </div>
  );
}
